import type {
  GetMyQuoteRequestsParams,
  GetPartnersParams,
  GetRecommendedPartnersParams,
} from '~/api/data-source/partners';
import { partnersRepository } from '~/api/repository/partners';

export const PARTNERS_QUERY_KEY = {
  all: ['partners'] as const,

  /** 파트너 목록 조회 */
  list: (params: GetPartnersParams) => ({
    queryKey: ['/api/market-place/partners', params] as const,
    queryFn: () => partnersRepository.getPartners(params),
  }),

  /** 파트너 목록 무한 스크롤 조회 */
  infiniteList: (params: Omit<GetPartnersParams, 'lastId'>) => ({
    queryKey: ['/api/market-place/partners/infinite', params] as const,
    queryFn: (lastId?: number) =>
      partnersRepository.getPartners({ ...params, lastId }),
  }),

  /** 추천 파트너 목록 조회 */
  recommended: (params: GetRecommendedPartnersParams) => ({
    queryKey: ['/api/market-place/partners/recommended', params] as const,
    queryFn: () => partnersRepository.getRecommendedPartners(params),
  }),

  /** 파트너 상세 조회 */
  detail: (partnerId: number) => ({
    queryKey: [`/api/market-place/partners/${partnerId}`] as const,
    queryFn: () => partnersRepository.getPartnerDetail(partnerId),
  }),

  /** 내 견적 요청 목록 조회 */
  myQuoteRequests: (params: GetMyQuoteRequestsParams) => ({
    queryKey: ['/api/market-place/partners/my/quote-requests', params] as const,
    queryFn: () => partnersRepository.getMyQuoteRequests(params),
  }),

  /** 견적 요청 상세 조회 */
  quoteRequestDetail: (quoteRequestId: number) => ({
    queryKey: [
      `/api/market-place/partners/my/quote-requests/${quoteRequestId}`,
    ] as const,
    queryFn: () => partnersRepository.getQuoteRequestDetail(quoteRequestId),
  }),

  /** 견적 요청 요약 조회 */
  quoteRequestSummary: () => ({
    queryKey: ['/api/market-place/partners/quote-requests/summary'] as const,
    queryFn: () => partnersRepository.getQuoteRequestSummary(),
  }),
};

/** 클라이언트 전용 캐시 키 (API 호출 없음) */
export const PARTNERS_CACHE_KEY = {
  /** 선택된 파트너 목록 (membership 폼에서 checkin으로 전달용) */
  selectedPartners: () =>
    [...PARTNERS_QUERY_KEY.all, 'selectedPartners'] as const,

  /** 멤버십 폼 데이터 (membership-register에서 membership-checkin으로 전달용) */
  membershipFormData: () =>
    [...PARTNERS_QUERY_KEY.all, 'membershipFormData'] as const,

  /** 일반 견적 폼 데이터 (register에서 checkin으로 전달용) */
  registerFormData: () =>
    [...PARTNERS_QUERY_KEY.all, 'registerFormData'] as const,
};

/** Mutation Keys (캐시 무효화에 사용) */
export const PARTNERS_MUTATION_KEY = {
  createQuoteRequest: () =>
    [...PARTNERS_QUERY_KEY.all, 'createQuoteRequest'] as const,
  acceptQuote: () => [...PARTNERS_QUERY_KEY.all, 'acceptQuote'] as const,
  rejectQuote: () => [...PARTNERS_QUERY_KEY.all, 'rejectQuote'] as const,
};
